import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { SheetShapeDeepFragment } from '../../../gql/graphql';
import { toMultiPolygon2 } from '../../../domain/geometry/algorithms/util/type-mapping';
import { LineSegment2, Point2 } from '../../../domain/geometry/geometric-types';
import {
  Viewport,
  getPointInPdfCoordinateSystem,
  getPointInViewerCoordinateSystem,
  SheetViewerContext,
} from '../../common/SheetViewer';
import { SheetPolygon } from './SheetPolygon';
import { SheetEdge } from './SheetEdge';
import { SheetVertex } from './SheetVertex';
import { SheetsViewerV2Context } from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.context';
import { useViewerMode, ViewerMode } from 'src/components/common/viewer-mode';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';
import { VIEWER_TOP_OFFSET } from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.config';

export const MoveOrCopySheetShapes = ({
  shapes,
  onResult,
}: {
  shapes: SheetShapeDeepFragment[];
  onResult: (translationVector: Point2) => void;
}) => {
  const [startPoint, setStartPoint] = useState<Point2 | null>(null);
  const [intermediatePoint, setIntermediatePoint] = useState<Point2 | null>(
    null
  );

  const { viewerMode } = useViewerMode();
  const sheetViewer = useContext(SheetViewerContext);
  const sheetViewerV2 = useContext(SheetsViewerV2Context);

  const isV2 = viewerMode === ViewerMode.SheetsV2 && sheetViewerV2;

  const viewerRef = isV2
    ? sheetViewerV2?.viewer.elementRef
    : sheetViewer?.viewerRef;
  const page = isV2
    ? sheetViewerV2?.activeSheet?.data?.page
    : sheetViewer?.page;
  const addViewportChangeHandler = isV2
    ? undefined
    : sheetViewer?.addViewportChangeHandler;
  const removeViewportChangeHandler = isV2
    ? undefined
    : sheetViewer?.removeViewportChangeHandler;

  const [viewerViewport, setViewerViewport] = useState<Viewport | null>(null);

  // Helper function to get point from v2 viewer
  const getPointFromV2Viewer = useCallback(
    (clientX: number, clientY: number): Point2 | null => {
      if (
        !sheetViewerV2 ||
        !sheetViewerV2.viewer.aggregated ||
        !sheetViewerV2.activeSheet?.data
      ) {
        return null;
      }
      const viewer = sheetViewerV2.viewer.aggregated.viewer;
      const converter = new ApiCoordinatesConverter(
        viewer,
        sheetViewerV2.activeSheet.data.viewport
      );
      const result = viewer.impl.clientToWorld(
        clientX,
        clientY - VIEWER_TOP_OFFSET
      );
      if (result && result.point) {
        const api = converter.convertToApiPoint(result.point);
        return [api.x, api.y];
      }
      return null;
    },
    [sheetViewerV2]
  );

  // Helper function to get point from v1 viewer
  const getPointFromV1Viewer = useCallback(
    (clientX: number, clientY: number): Point2 | null => {
      if (!viewerViewport || !page || !viewerRef?.current) {
        return null;
      }
      // Type guard to ensure page is PageCallback
      if (typeof page === 'number') {
        return null;
      }
      return getPointInPdfCoordinateSystem(
        [clientX, clientY],
        viewerViewport.offset,
        viewerViewport.scale,
        page,
        viewerRef.current
      );
    },
    [viewerViewport, page, viewerRef]
  );

  // Get the point in the DOM coordinate system
  const getPointInDomCoordinateSystem = useCallback(
    (pointInDom: Point2) => {
      if (isV2) {
        if (
          !sheetViewerV2 ||
          !sheetViewerV2.viewer.aggregated ||
          !sheetViewerV2.activeSheet?.data
        )
          throw new Error('Viewer not ready');

        const converter = new ApiCoordinatesConverter(
          sheetViewerV2.viewer.aggregated.viewer,
          sheetViewerV2.activeSheet.data.viewport
        );
        const world = converter.convertToAutodeskPoint({
          x: pointInDom[0],
          y: pointInDom[1],
        });
        const screen =
          sheetViewerV2.viewer.aggregated.viewer.worldToClient(world);
        return [screen.x, screen.y] as Point2;
      }

      if (!viewerViewport || !page || !viewerRef?.current) {
        throw new Error('Viewer not ready');
      }
      // Type guard to ensure page is PageCallback
      if (typeof page === 'number') {
        throw new Error('Invalid page type');
      }
      return getPointInViewerCoordinateSystem(
        pointInDom,
        viewerViewport.offset,
        viewerViewport.scale,
        page
      );
    },
    [isV2, sheetViewerV2, viewerViewport, page, viewerRef]
  );

  useEffect(() => {
    if (isV2 || !addViewportChangeHandler || !removeViewportChangeHandler)
      return;
    const onViewportChange = (vp: Viewport) => {
      setViewerViewport(vp);
    };
    addViewportChangeHandler(onViewportChange);
    return () => {
      removeViewportChangeHandler(onViewportChange);
    };
  }, [isV2, addViewportChangeHandler, removeViewportChangeHandler]);

  // Helper function to handle point selection
  const handlePointSelection = useCallback(
    (clientX: number, clientY: number) => {
      let newPoint: Point2 | null = null;

      if (isV2) {
        newPoint = getPointFromV2Viewer(clientX, clientY);
      } else {
        newPoint = getPointFromV1Viewer(clientX, clientY);
      }

      if (!newPoint) {
        return null;
      }

      if (!startPoint) {
        setStartPoint(newPoint);
        return null;
      } else {
        const translationVector: Point2 = [
          newPoint[0] - startPoint[0],
          newPoint[1] - startPoint[1],
        ];
        onResult(translationVector);
        setStartPoint(null);
        return translationVector;
      }
    },
    [isV2, getPointFromV2Viewer, getPointFromV1Viewer, startPoint, onResult]
  );

  useEffect(() => {
    const currentViewer = viewerRef?.current;
    if (!currentViewer) {
      return;
    }

    const handlePageClick = (event: MouseEvent) => {
      if (event.button !== 0) return;

      const result = handlePointSelection(event.clientX, event.clientY);
      if (result !== null) {
        event.stopPropagation();
      }
    };

    currentViewer.addEventListener('click', handlePageClick);
    return () => {
      currentViewer.removeEventListener('click', handlePageClick);
    };
  }, [handlePointSelection, viewerRef]);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      let newPoint: Point2 | null = null;

      if (isV2) {
        newPoint = getPointFromV2Viewer(event.clientX, event.clientY);
      } else {
        newPoint = getPointFromV1Viewer(event.clientX, event.clientY);
      }

      if (!newPoint) {
        return;
      }
      setIntermediatePoint(newPoint);
    },
    [isV2, getPointFromV2Viewer, getPointFromV1Viewer]
  );

  useEffect(() => {
    const current = viewerRef?.current;
    if (!current) {
      return;
    }
    current.addEventListener('pointermove', handlePointerMove);
    return () => {
      current.removeEventListener('pointermove', handlePointerMove);
    };
  }, [handlePointerMove, viewerRef]);

  const translatedShapes = useMemo(() => {
    if (!startPoint || !intermediatePoint) return [];

    const translation = {
      x: intermediatePoint[0] - startPoint[0],
      y: intermediatePoint[1] - startPoint[1],
    };

    return (
      <Box
        width={'100%'}
        height={'100%'}
        position={'absolute'}
        pointerEvents={'none'}
      >
        {shapes.map((shape) => {
          if ('sheetShapePolygon' in shape && shape.sheetShapePolygon) {
            return IntermediatePolygonShape(
              shape,
              translation,
              getPointInDomCoordinateSystem
            );
          } else if ('sheetShapeLine' in shape && shape.sheetShapeLine) {
            return IntermediateLineShape(
              shape,
              translation,
              getPointInDomCoordinateSystem
            );
          } else if ('sheetShapePoint' in shape && shape.sheetShapePoint) {
            return IntermediatePointShape(
              shape,
              translation,
              getPointInDomCoordinateSystem
            );
          }
        })}
      </Box>
    );
  }, [startPoint, intermediatePoint, shapes, getPointInDomCoordinateSystem]);

  return <>{translatedShapes}</>;
};

const IntermediatePolygonShape = (
  shape: SheetShapeDeepFragment,
  translation: { x: number; y: number },
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2
) => {
  const clonedShape: SheetShapeDeepFragment = JSON.parse(JSON.stringify(shape));
  clonedShape.sheetShapePolygon?.multipolygon.polygons.forEach((polygon) => {
    polygon.exterior.points.forEach((point) => {
      point.x += translation.x;
      point.y += translation.y;
    });
    polygon.interiors.forEach((interior) => {
      interior.points.forEach((point) => {
        point.x += translation.x;
        point.y += translation.y;
      });
    });
  });

  return clonedShape.sheetShapePolygon?.multipolygon ? (
    <SheetPolygon
      key={shape.id}
      multipolygon={toMultiPolygon2(
        clonedShape.sheetShapePolygon?.multipolygon
      )}
      getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      fill={'orange.300'}
    />
  ) : null;
};

const IntermediateLineShape = (
  shape: SheetShapeDeepFragment,
  translation: { x: number; y: number },
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2
) => {
  const clonedShape: SheetShapeDeepFragment = JSON.parse(JSON.stringify(shape));
  clonedShape.sheetShapeLine?.points.forEach((point) => {
    point.x += translation.x;
    point.y += translation.y;
  });

  const edges: LineSegment2[] = [];

  if (clonedShape.sheetShapeLine?.points) {
    for (let i = 0; i < clonedShape.sheetShapeLine.points.length - 1; i++) {
      edges.push([
        [
          clonedShape.sheetShapeLine.points[i].x,
          clonedShape.sheetShapeLine.points[i].y,
        ],
        [
          clonedShape.sheetShapeLine.points[i + 1].x,
          clonedShape.sheetShapeLine.points[i + 1].y,
        ],
      ]);
    }
  }

  return edges.map((edge, index) => (
    <SheetEdge
      key={shape.id + index}
      segment={edge}
      cursor={'pointer'}
      getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      backgroundColor={'orange.300'}
    />
  ));
};

const IntermediatePointShape = (
  shape: SheetShapeDeepFragment,
  translation: { x: number; y: number },
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2
) => {
  const clonedShape: SheetShapeDeepFragment = JSON.parse(JSON.stringify(shape));
  if (clonedShape.sheetShapePoint) {
    clonedShape.sheetShapePoint.point.x += translation.x;
    clonedShape.sheetShapePoint.point.y += translation.y;

    return (
      <SheetVertex
        key={shape.id}
        point={[
          clonedShape.sheetShapePoint.point.x,
          clonedShape.sheetShapePoint.point.y,
        ]}
        getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
        theme={'default'}
        backgroundColor={'orange.300'}
        borderColor={'orange.300'}
      />
    );
  }
};
